/**
 * Greek message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: informal εσύ (locked in l10n/locales.md).
 *
 * Platform terms follow Greek UI conventions (Θέμα for theme, συνομιλία
 * for conversation, σύζευξη for pair, απόρριψη for dismiss, δέκτης for a
 * receiving surface); "Glance" is the untranslated feature brand name
 * (glossary D1) and "κλιπ" is the locked loanword rendered in Greek
 * script (D2).
 *
 * Plural notes: Greek is a {one, other} CLDR locale (English-like
 * boundaries, same shape as de/nl), and its count nouns inflect
 * (χαρακτήρας → χαρακτήρες, αποτέλεσμα → αποτελέσματα), so every
 * count-bearing function selects through `selectPlural("el", …)`.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Συνδεδεμένο",
    statusReconnecting: "Επανασύνδεση…",
    statusDisconnected: "Αποσυνδεδεμένο",
    openSettings: "Άνοιγμα ρυθμίσεων",
    pairedFallbackDevice: "το τηλέφωνό σου",
    publicModeToggleEnable: "Απόκρυψη περιεχομένου ειδοποιήσεων",
    publicModeToggleDisable: "Εμφάνιση περιεχομένου ειδοποιήσεων",
  },

  notifications: {
    replyAriaLabel: "Απάντηση",
    replyPlaceholder: "Απάντηση…",
    replySend: "Αποστολή",
    sent: "Στάλθηκε",
    failedToSend: "Αποτυχία αποστολής",
    unread: "Μη αναγνωσμένη",
    dismiss: "Απόρριψη",
    notificationImageAlt: "Εικόνα ειδοποίησης",
  },

  conversations: {
    replyAriaLabel: "Απάντηση",
    replyPlaceholder: "Απάντηση…",
    replySend: "Αποστολή",
    sent: "Στάλθηκε",
    failedToSend: "Αποτυχία αποστολής",
    dismiss: "Απόρριψη συνομιλίας",
    messagesListLabel: "Μηνύματα σε αυτή τη συνομιλία",
    deviceOwner: "Εσύ",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Κανένα μη αναγνωσμένο μήνυμα"
        : `${count} ${selectPlural("el", count, {
            one: "μη αναγνωσμένο μήνυμα",
            other: "μη αναγνωσμένα μηνύματα",
          })}`,
  },

  list: {
    today: "Σήμερα",
    yesterday: "Χθες",
    searchNotifications: "Αναζήτηση ειδοποιήσεων",
    clearSearch: "Διαγραφή αναζήτησης",
    clearAll: "Διαγραφή όλων",
    markAllRead: "Επισήμανση όλων ως αναγνωσμένων",
    groupCheckboxLabel: "Ομαδοποίηση",
    noNotificationsTitle: "Δεν υπάρχουν ειδοποιήσεις ακόμη",
    noNotificationsDesc: "Νέα μηνύματα που κατοπτρίζονται από το τηλέφωνό σου θα εμφανίζονται εδώ.",
    noMatchesTitle: "Κανένα αποτέλεσμα",
    notificationsList: "Ειδοποιήσεις",
    privacyAppList: "Ειδοποιήσεις ανά εφαρμογή",
    clear: "Διαγραφή",
    clearMatches: (count: number): string =>
      `Διαγραφή ${count} ${selectPlural("el", count, { one: "αποτελέσματος", other: "αποτελεσμάτων" })}`,
    noMatchesDesc: (query: string): string => `Καμία ειδοποίηση δεν αντιστοιχεί στο «${query}».`,
    appNotificationsGroup: (appName: string): string => `Ειδοποιήσεις ${appName}`,
    dismissAllFrom: (appName: string): string => `Απόρριψη όλων από ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("el", count, { one: "μήνυμα", other: "μηνύματα" })
          : kind === "calls"
            ? selectPlural("el", count, { one: "κλήση", other: "κλήσεις" })
            : selectPlural("el", count, { one: "ειδοποίηση", other: "ειδοποιήσεις" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "απόρριψη",
    kbdNavigate: "πλοήγηση",
    kbdOrSeparator: "ή",
  },

  settings: {
    back: "Πίσω στις ειδοποιήσεις",
    settings: "Ρυθμίσεις",
    appearance: "Εμφάνιση",
    theme: "Θέμα",
    themeAuto: "Αυτόματο",
    themeLight: "Ανοιχτό",
    themeDark: "Σκούρο",
    language: "Γλώσσα",
    languageSystemDefault: "Προεπιλογή συστήματος",
    notificationsSection: "Ειδοποιήσεις",
    osNotifications: "Ειδοποιήσεις συστήματος",
    callNotifications: "Ειδοποιήσεις κλήσεων",
    privacySection: "Απόρρητο",
    analyticsToggle: "Ανώνυμα στατιστικά χρήσης",
    analyticsToggleDescription: "Μόνο μετρήσεις και ορόσημα — ποτέ οι ειδοποιήσεις σας.",
    connection: "Σύνδεση",
    status: "Κατάσταση",
    statusConnected: "Συνδεδεμένο",
    statusReconnecting: "Επανασύνδεση…",
    statusDisconnected: "Αποσυνδεδεμένο",
    disconnectFromPhone: "Αποσύνδεση από το τηλέφωνο",
    disconnectConfirm: "Σίγουρα; Αυτό θα καταργήσει τη σύζευξη της συσκευής σου.",
    yesDisconnect: "Ναι, αποσύνδεση",
    cancel: "Άκυρο",
    aboutAndSupport: "Σχετικά & Υποστήριξη",
    version: "Έκδοση",
    encryption: "Κρυπτογράφηση",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Χρειάζεσαι βοήθεια; Στείλε email στο __EMAIL__",
    disconnectFailed: "Αποτυχία αποσύνδεσης — δοκίμασε ξανά",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Η αποσύνδεση έγινε, αλλά ο τοπικός καθαρισμός απέτυχε: ${errorMsg}. Φόρτωσε ξανά τη σελίδα.`,
    disconnectLocalCleanupFailed:
      "Η αποσύνδεση έγινε, αλλά ο τοπικός καθαρισμός απέτυχε. Φόρτωσε ξανά τη σελίδα.",
  },

  pairing: {
    enterCodeInstruction: "Πληκτρολόγησε αυτόν τον κωδικό στην εφαρμογή Reflecto",
    pairedSuccessfully: "Η σύζευξη ολοκληρώθηκε!",
    pairingFailed: "Η σύζευξη απέτυχε",
    requestingPairingCode: "Αίτημα κωδικού σύζευξης…",
    codeExpired: "Ο κωδικός έληξε",
    tryAgain: "Δοκίμασε ξανά",
    failedToStartPairing: "Αποτυχία έναρξης σύζευξης",
    needAppText: "Χρειάζεσαι την εφαρμογή Android; Στείλε email στο __EMAIL__ για πρόσβαση.",
  },

  dnd: {
    title: "Μην ενοχλείτε",
    description: "Παύση ειδοποιήσεων σε αυτή τη συσκευή για την επόμενη ώρα.",
    snooze1h: "Παύση για 1 ώρα",
    snoozeActiveUntil: (formattedTime: string): string => `Ενεργό έως ${formattedTime}`,
    cancelSnooze: "Άκυρο",
  },

  connectors: {
    title: "Συνδεδεμένες υπηρεσίες",
    emptyBody:
      "Δεν υπάρχουν ακόμη συνδεδεμένες υπηρεσίες. Σύνδεσε το ChatGPT ή το Claude από τις ρυθμίσεις σύνδεσής τους και μετά ενέκρινε τον κωδικό στο τηλέφωνό σου.",
    scopeSend: "Μπορεί να στέλνει ειδοποιήσεις",
    added: (date: string): string => `Προστέθηκε ${date}`,
    lastUsed: (relative: string): string => `Τελευταία χρήση ${relative}`,
    neverUsed: "Δεν χρησιμοποιήθηκε ποτέ",
    revoke: "Αποσύνδεση",
    revokeConfirm: (label: string): string => `Αποσύνδεση ${label}; Θα χάσει την πρόσβαση αμέσως.`,
    revokeAction: "Αποσύνδεση",
    revokeConfirmLabel: (label: string): string => `Αποσύνδεση ${label}`,
    cancel: "Άκυρο",
    loadError: "Δεν ήταν δυνατή η φόρτωση των συνδεδεμένων υπηρεσιών. Δοκίμασε ξανά.",
    revokeError: "Δεν ήταν δυνατή η αποσύνδεση της υπηρεσίας. Δοκίμασε ξανά.",
    retry: "Επανάληψη",
  },

  feedback: {
    title: "Αποστολή σχολίων",
    close: "Κλείσιμο",
    messageLabel: "Το μήνυμά σου",
    messagePlaceholder: "Βρήκες σφάλμα, έχεις μια ιδέα ή κάτι δεν πάει καλά; Πες μας.",
    categoryLabel: "Κατηγορία",
    categoryBug: "Σφάλμα",
    categoryIdea: "Ιδέα",
    categoryOther: "Άλλο",
    emailLabel: "Email (προαιρετικό, μόνο αν θέλεις απάντηση)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Δεν θα σε προσθέσουμε σε καμία λίστα.",
    disclosure:
      "Το μήνυμά σου αποστέλλεται στον διακομιστή του Reflecto και καταγράφεται στο εσωτερικό μας σύστημα παρακολούθησης ζητημάτων. Επισυνάπτουμε την έκδοση της εφαρμογής σου και ένα αναγνωριστικό συσκευής που εκχωρείται κατά τη σύζευξη, ώστε να συσχετίζουμε τις αναφορές. Δεν βλέπουμε τις ειδοποιήσεις, τις επαφές ή άλλα προσωπικά σου δεδομένα. Το email είναι προαιρετικό — δώσε το μόνο αν θέλεις απάντηση. Δεν σε προσθέτουμε σε καμία λίστα.",
    submit: "Αποστολή σχολίων",
    sending: "Αποστολή…",
    success: "Ευχαριστούμε — το διαβάζει άνθρωπος, συνήθως εντός 24 ωρών",
    error: "Αποτυχία αποστολής. Δοκίμασε ξανά.",
    done: "Τέλος",
    sendFeedback: "Αποστολή σχολίων",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Κλείσιμο",
    aiSummaryTag: "Περίληψη AI",
    errorAi: "Δεν ήταν δυνατή η δημιουργία περίληψης αυτή τη στιγμή.",
    retryCta: "Δοκίμασε ξανά",
    downloadHeading: "Απόκτησε περιλήψεις AI",
    downloadBody:
      "Δες τι έχασες με μια περίληψη AI απευθείας στη συσκευή σου. Εκτελείται εξ ολοκλήρου τοπικά.",
    downloadCta: "Λήψη μοντέλου",
    downloadPrivacyNote: "Εφάπαξ λήψη. Ο διακομιστής δεν βλέπει ποτέ τις ειδοποιήσεις σου.",
    downloadingHeading: "Γίνεται λήψη μοντέλου…",
    caughtUp: "Είσαι πλήρως ενημερωμένος — τίποτα δεν χρειάζεται την προσοχή σου αυτή τη στιγμή.",
    needsYou: "Χρειάζεται προσοχή",
    quiet: "Άλλα",
    scanning: (pending: number): string => `Γίνεται σύνοψη ${pending}…`,
    jump: "Άνοιγμα ειδοποίησης",
    affordanceReply: "Απάντηση",
    affordanceOpen: "Άνοιγμα",
    affordanceCopyOtp: "Αντιγραφή κωδικού",
    buttonLabel: "Glance",
  },

  update: {
    title: "Διαθέσιμη ενημέρωση",
    description: "Μια νέα έκδοση του Reflecto είναι έτοιμη. Φόρτωσε ξανά για ενημέρωση.",
    reload: "Επαναφόρτωση",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Ονομάσαμε αυτή τη συσκευή «${name}».`,
    cardLooksGood: "Μια χαρά",
    cardEdit: "Επεξεργασία",
    cardAriaLabel: "Επιβεβαίωση ονόματος συσκευής",
    renameTitle: "Μετονομασία αυτής της συσκευής",
    renameLabel: "Όνομα συσκευής",
    renameSave: "Αποθήκευση",
    renameCancel: "Άκυρο",
    renameFailed: "Η μετονομασία απέτυχε. Δοκίμασε ξανά.",
  },

  collision: {
    title: (name: string): string => `Έχεις ήδη μια «${name}»`,
    subtitle: "Πώς να ονομάσουμε αυτή;",
    chipWork: (name: string): string => `Εργασία ${name}`,
    chipHome: (name: string): string => `Σπίτι ${name}`,
    chipPersonal: (name: string): string => `Προσωπικό ${name}`,
    customLabel: "Προσαρμοσμένο όνομα",
    save: "Αποθήκευση",
    useAnyway: "Χρήση του αρχικού ονόματος ούτως ή άλλως",
    saveFailed: "Δεν ήταν δυνατή η αποθήκευση του νέου ονόματος. Δοκίμασε ξανά.",
    ariaLabel: "Επίλυση διπλότυπου ονόματος συσκευής",
  },

  clipboard: {
    title: "Πρόχειρο",
    empty: "Τα κλιπ από τις άλλες συσκευές σου εμφανίζονται εδώ. Πάτησε για αντιγραφή.",
    from: (label: string): string => `από ${label}`,
    copy: "Αντιγραφή στο πρόχειρο",
    copied: "Αντιγράφηκε",
    reveal: "Εμφάνιση",
    hide: "Απόκρυψη",
    maskedHint: "Το περιεχόμενο είναι κρυμμένο — πάτησε Εμφάνιση για να το δεις",
    sensitiveChip: "Ευαίσθητο",
    cloudChip: "από το διακριτικό API σου",
    sendPlaceholder: "Επικόλλησε κείμενο για αποστολή…",
    sendCta: "Αποστολή",
    sendSensitiveToggle: "Επισήμανση ως ευαίσθητο",
    sendSuccess: (n: number): string =>
      `Στάλθηκε σε ${n} ${selectPlural("el", n, { one: "συσκευή", other: "συσκευές" })}.`,
    sendPartial: (n: number, total: number): string =>
      `Παραδόθηκε σε ${n}/${total} ${selectPlural("el", total, { one: "συσκευή", other: "συσκευές" })}.`,
    sendNoDevices: "Δεν υπάρχουν συζευγμένες συσκευές για αποστολή.",
    sendFailed: "Αποτυχία αποστολής. Δοκίμασε ξανά.",
    sendEmpty: "Δεν υπάρχει τίποτα για αποστολή.",
    dockExpand: "Εμφάνιση ιστορικού προχείρου",
    dockCollapse: "Σύμπτυξη",
    dockSendTitle: "Αποστολή στις συσκευές σου",
    dockFromClipboard: (chars: number): string =>
      `Από το πρόχειρο · ${chars} ${selectPlural("el", chars, {
        one: "χαρακτήρας",
        other: "χαρακτήρες",
      })}`,
    dockSend: (n: number): string => `Αποστολή σε ${n}`,
    dockCancel: "Άκυρο",
    dockRecentHeading: "Πρόσφατα",
    dockEmptyClipboard: "Το πρόχειρό σου είναι άδειο.",
    shareTitle: "Αποστολή στις συσκευές σου",
    shareReview:
      "Έλεγξε τι στέλνεις. Αποστέλλεται στις συζευγμένες συσκευές σου, κρυπτογραφημένο από άκρο σε άκρο.",
    shareSend: "Αποστολή",
    shareCancel: "Άκυρο",
    shareNotPaired: "Κάνε πρώτα σύζευξη μιας συσκευής για να στείλεις κοινόχρηστο κείμενο.",
    shareOpenApp: "Άνοιγμα του Reflecto",
  },
  install: {
    title: "Εγκατάσταση του Reflecto",
    body: "Πρόσθεσέ το στην αρχική οθόνη για ταχύτερη πρόσβαση.",
    cta: "Εγκατάσταση",
    dismissAria: "Απόρριψη προτροπής εγκατάστασης",
  },
  common: {
    loading: "Φόρτωση",
  },
  errors: {
    notPaired: "Χωρίς σύζευξη",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} σε ${os}`,
    webFallback: "Πρόγραμμα περιήγησης",
  },

  palette: {
    placeholder: "Πληκτρολόγησε μια εντολή…",
    noResults: "Δεν υπάρχουν αντίστοιχες εντολές",
    ariaLabel: "Παλέτα εντολών",
    clearAll: "Διαγραφή όλων των ειδοποιήσεων",
    glance: "Περίληψη Glance",
    cancelSnooze: "Ακύρωση παύσης",
    copyClip: "Αντιγραφή του τελευταίου κλιπ που ελήφθη",
  },
} satisfies MessagesShape;
